import { useEffect, useState } from "react";
import { Search, Users, CreditCard, ShieldCheck, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type UserRow = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
};

type HouseholdMemberRow = {
  user_id: string;
  household_id: string;
  role: string;
};

type HouseholdRow = {
  id: string;
  name: string;
  owner_id: string;
};

type SubscriptionRow = {
  household_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  billing_exempt: boolean | null;
  created_at: string;
  updated_at: string;
};

type MergedUser = {
  id: string;
  display_name: string | null;
  created_at: string;
  household_name: string | null;
  household_role: string | null;
  sub_status: string | null;
  stripe_customer_id: string | null;
  billing_exempt: boolean;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
};

const statusColor = (status: string | null, exempt: boolean) => {
  if (exempt) return "border-emerald-500/40 bg-emerald-500/15 text-emerald-400";
  switch (status) {
    case "active": return "border-emerald-500/40 bg-emerald-500/15 text-emerald-400";
    case "trialing": return "border-blue-500/40 bg-blue-500/15 text-blue-400";
    case "canceled":
    case "cancelled": return "border-red-500/40 bg-red-500/15 text-red-400";
    case "past_due": return "border-amber-500/40 bg-amber-500/15 text-amber-400";
    case "pending": return "border-muted-foreground/40 bg-muted/30 text-muted-foreground";
    default: return "border-border bg-card text-muted-foreground";
  }
};

const statusLabel = (status: string | null, exempt: boolean) => {
  if (exempt) return "Billing Exempt";
  if (!status) return "No Subscription";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export default function UsersSubscribersPanel() {
  const [users, setUsers] = useState<MergedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = async () => {
    setLoading(true);

    const [profilesRes, membersRes, householdsRes, subsRes] = await Promise.all([
      supabase.from("profiles").select("id, display_name, avatar_url, created_at").order("created_at", { ascending: false }).limit(500),
      supabase.from("household_members").select("user_id, household_id, role"),
      supabase.from("households").select("id, name, owner_id"),
      supabase.from("subscription_records").select("*"),
    ]);

    if (profilesRes.error) {
      toast.error("Failed to load users");
      setLoading(false);
      return;
    }

    const profiles = (profilesRes.data || []) as UserRow[];
    const members = (membersRes.data || []) as HouseholdMemberRow[];
    const households = (householdsRes.data || []) as HouseholdRow[];
    const subs = (subsRes.data || []) as SubscriptionRow[];

    const householdMap = new Map(households.map(h => [h.id, h]));
    const memberMap = new Map(members.map(m => [m.user_id, m]));
    const subMap = new Map(subs.map(s => [s.household_id, s]));

    const merged: MergedUser[] = profiles.map(profile => {
      const membership = memberMap.get(profile.id);
      const household = membership ? householdMap.get(membership.household_id) : null;
      const sub = household ? subMap.get(household.id) : null;

      return {
        id: profile.id,
        display_name: profile.display_name,
        created_at: profile.created_at,
        household_name: household?.name || null,
        household_role: membership?.role || null,
        sub_status: sub?.status || null,
        stripe_customer_id: sub?.stripe_customer_id || null,
        billing_exempt: Boolean(sub?.billing_exempt),
        cancel_at_period_end: Boolean(sub?.cancel_at_period_end),
        current_period_end: sub?.current_period_end || null,
      };
    });

    setUsers(merged);
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const filtered = users.filter(u => {
    const text = `${u.display_name || ""} ${u.id} ${u.household_name || ""} ${u.stripe_customer_id || ""}`.toLowerCase();
    const matchSearch = !search.trim() || text.includes(search.trim().toLowerCase());

    let matchStatus = true;
    if (statusFilter === "active") matchStatus = u.sub_status === "active" || u.billing_exempt;
    else if (statusFilter === "trialing") matchStatus = u.sub_status === "trialing";
    else if (statusFilter === "canceled") matchStatus = u.sub_status === "canceled" || u.sub_status === "cancelled";
    else if (statusFilter === "none") matchStatus = !u.sub_status && !u.billing_exempt;
    else if (statusFilter === "exempt") matchStatus = u.billing_exempt;

    const matchRole = roleFilter === "all" || u.household_role === roleFilter;

    return matchSearch && matchStatus && matchRole;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.sub_status === "active" || u.billing_exempt).length,
    trialing: users.filter(u => u.sub_status === "trialing").length,
    noSub: users.filter(u => !u.sub_status && !u.billing_exempt).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="rounded-full border-2 border-primary border-t-transparent h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Stats row */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Total Users", value: stats.total, icon: Users },
          { label: "Active / Exempt", value: stats.active, icon: ShieldCheck },
          { label: "Trialing", value: stats.trialing, icon: CreditCard },
          { label: "No Subscription", value: stats.noSub, icon: Users },
        ].map(s => (
          <div key={s.label} className="glass-card p-5">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{s.label}</p>
              <s.icon className="h-4 w-4 text-primary" />
            </div>
            <p className="font-heading text-2xl font-bold text-foreground">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="glass-card p-5">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="md:col-span-2">
            <Label className="mb-2 block">Search</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, ID, household, or Stripe ID" className="pl-10" />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">Subscription</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active / Exempt</SelectItem>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
                <SelectItem value="none">No Subscription</SelectItem>
                <SelectItem value="exempt">Billing Exempt</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-2 block">Role</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="learner">Learner</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{filtered.length} user{filtered.length !== 1 ? "s" : ""}</p>
          <Button variant="ghost" size="sm" onClick={load} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Household</th>
              <th className="px-4 py-3">Subscription</th>
              <th className="px-4 py-3">Period End</th>
              <th className="px-4 py-3">Joined</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-border/50 hover:bg-card/50 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium text-foreground">{u.display_name || "Unnamed"}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate max-w-[180px]">{u.id}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="text-muted-foreground capitalize">{u.household_role || "—"}</span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-foreground">{u.household_name || "—"}</p>
                  {u.household_role && <p className="text-xs text-muted-foreground capitalize">{u.household_role}</p>}
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={cn("text-xs", statusColor(u.sub_status, u.billing_exempt))}>
                    {statusLabel(u.sub_status, u.billing_exempt)}
                  </Badge>
                  {u.cancel_at_period_end && u.sub_status === "active" && (
                    <p className="mt-0.5 text-[10px] text-amber-400">Cancels at period end</p>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {u.current_period_end ? new Date(u.current_period_end).toLocaleDateString() : "—"}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No users match your filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
